import React, { useCallback, useState } from 'react';

import {
  Button,
  FormGroup,
  H5,
  Intent,
  NumericInput,
  Position,
  TextArea,
} from '@blueprintjs/core';

import { computeIMC } from '../../utils';

import { Gender, RacialDenomination, WorkflowPerson } from '../../types';

import Unit from '../Unit';

import { ItemActions, LineInfo } from './styles';

interface PersonDataProps {
  person: WorkflowPerson;
  onChange: (person: WorkflowPerson) => void;
  onSave?: (person: WorkflowPerson) => void;
}

const PersonData = ({ person, onChange, onSave }: PersonDataProps) => {
  const [heightTempValue, setHeightTempValue] = useState(
    person.height.toString(),
  );
  const [weightTempValue, setWeightTempValue] = useState(
    person.weight.toString(),
  );

  const handleValueChange = (
    type: 'height' | 'weight',
    valueAsNumber: number,
    valueAsString: string,
  ) => {
    if (type === 'height') {
      setHeightTempValue(valueAsString);
    } else {
      setWeightTempValue(valueAsString);
    }

    if (!isNaN(valueAsNumber)) {
      onChange({
        ...person,
        [type]: valueAsNumber,
      });
    }
  };

  const onSaveAction = useCallback(() => {
    if (!!onSave) {
      onSave(person);
    }
  }, [person, onSave]);

  return (
    <>
      <FormGroup
        helperText={'Informe a sua altura...'}
        intent={Intent.PRIMARY}
        label={'Altura'}
        labelFor="height-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="height-input"
          intent={Intent.PRIMARY}
          leftIcon="rect-height"
          value={heightTempValue}
          onValueChange={(valueAsNumber, valueAsString) =>
            handleValueChange('height', valueAsNumber, valueAsString)
          }
          stepSize={0.01}
          minorStepSize={0.01}
          min={0}
          max={300}
          rightElement={<Unit>cm</Unit>}
          buttonPosition={Position.LEFT}
        />
      </FormGroup>
      <FormGroup
        helperText={'Informe a seu peso...'}
        intent={Intent.PRIMARY}
        label={'Peso'}
        labelFor="weight-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="weight-input"
          intent={Intent.PRIMARY}
          leftIcon="horizontal-inbetween"
          value={weightTempValue}
          onValueChange={(valueAsNumber, valueAsString) =>
            handleValueChange('weight', valueAsNumber, valueAsString)
          }
          stepSize={0.001}
          minorStepSize={0.001}
          min={0}
          max={500}
          rightElement={<Unit>kg</Unit>}
          buttonPosition={Position.LEFT}
        />
      </FormGroup>
      <FormGroup>
        <LineInfo data-testid="imc-div">
          <H5>IMC: </H5>
          <>
            {person.weight !== 0 && person.height !== 0 ? (
              <H5>{computeIMC(person).toFixed(2)}%</H5>
            ) : (
              <H5>?</H5>
            )}
          </>
        </LineInfo>
      </FormGroup>
      <FormGroup
        helperText={'Informe a sua idade...'}
        intent={Intent.PRIMARY}
        label={'Idade'}
        labelFor="age-input"
        labelInfo={'(obrigatório)'}
      >
        <NumericInput
          inputMode="numeric"
          selectAllOnFocus
          fill
          id="age-input"
          intent={Intent.PRIMARY}
          leftIcon="new-person"
          value={person.age}
          onValueChange={(age) =>
            onChange({
              ...person,
              age,
            })
          }
          stepSize={1}
          min={0}
          max={150}
          rightElement={<Unit>anos</Unit>}
          buttonPosition={Position.LEFT}
        />
      </FormGroup>
      <FormGroup
        helperText={'Informe a seu gênero...'}
        intent={Intent.PRIMARY}
        label={'Gênero'}
        labelFor="gender-input"
        labelInfo={'(obrigatório)'}
      >
        <div className="bp5-html-select bp5-fill">
          <select
            id="gender-input"
            value={person.gender}
            onChange={(e) => {
              onChange({
                ...person,
                gender: Gender[String(e.target.value) as keyof typeof Gender],
              });
            }}
          >
            <option value={Gender.UNDEFINED}>Escolha um item...</option>
            <option value={Gender.MALE}>Masculino</option>
            <option value={Gender.FEMALE}>Feminino</option>
          </select>
          <span className="bp5-icon bp5-icon-double-caret-vertical"></span>
        </div>
      </FormGroup>
      <FormGroup
        helperText={'Informe a sua denominação racial...'}
        intent={Intent.PRIMARY}
        label={'Denominação racial'}
        labelFor="racial-denomination-input"
        labelInfo={'(obrigatório)'}
      >
        <div className="bp5-html-select bp5-fill">
          <select
            id="racial-denomination-input"
            value={person.racialDenomination}
            onChange={(e) => {
              onChange({
                ...person,
                racialDenomination:
                  RacialDenomination[
                    String(e.target.value) as keyof typeof RacialDenomination
                  ],
              });
            }}
          >
            <option value={RacialDenomination.UNDEFINED}>
              Escolha um item...
            </option>
            <option value={RacialDenomination.WHITE}>Branco</option>
            <option value={RacialDenomination.BROWN}>Pardo</option>
            <option value={RacialDenomination.BLACK}>Preto</option>
            <option value={RacialDenomination.ORIENTAL}>Oriental</option>
            <option value={RacialDenomination.INDIGENOUS}>Indígena</option>
          </select>
          <span className="bp5-icon bp5-icon-double-caret-vertical"></span>
        </div>
      </FormGroup>
      <FormGroup
        helperText={'Informações extras...'}
        intent={Intent.PRIMARY}
        label={'Informações extras'}
        labelFor="extra-input"
      >
        <TextArea
          fill
          placeholder="Informações extras"
          growVertically={true}
          value={person.extraDescription}
          onChange={(e) =>
            onChange({
              ...person,
              extraDescription: e.target.value,
            })
          }
        />
      </FormGroup>
      {!!onSave && (
        <ItemActions>
          <Button intent={Intent.PRIMARY} onClick={onSaveAction}>
            Salvar dados Pessoais
          </Button>
        </ItemActions>
      )}
    </>
  );
};

export default PersonData;
